import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RefreshCw, Send, Loader2, Info, TrendingUp } from 'lucide-react';

const API_URL = '/api';

const FollowUpSystem = () => {
  const [stats, setStats] = useState({ received: 0, sent: 0 });
  const [followupStats, setFollowupStats] = useState({
    total_sent: 0,
    responses: 0,
    followups_sent: 0,
    pending_followups: 0,
    response_rate: 0
  });
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [isGmailConnected, setIsGmailConnected] = useState(false);
  const [apiKey, setApiKey] = useState('');

  useEffect(() => {
    loadStats();
    loadFollowupStats();
    
    const savedApiKey = localStorage.getItem('apiKey');
    if (savedApiKey) setApiKey(savedApiKey);
    
    const savedGmail = localStorage.getItem('gmailEmail');
    setIsGmailConnected(!!savedGmail);
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/stats`);
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadFollowupStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/followup/stats`);
      if (response.data.success) {
        setFollowupStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load follow-up stats:', error);
    }
  };

  const checkForResponses = async () => {
    if (!isGmailConnected) {
      alert('Please connect Gmail in Settings first!');
      return;
    }

    setLoading(true);
    setLoadingText('🔍 Checking Gmail inbox for responses...');

    try {
      const response = await axios.post(`${API_URL}/followup/check-responses`, {
        sessionId: 'react-session'
      });

      if (response.data.success) {
        loadFollowupStats();
        
        if (response.data.responses_found > 0) {
          alert(`✅ Responses Found!\n\n` +
                `Found ${response.data.responses_found} new responses!\n` +
                `These emails have been marked and won't receive follow-ups.`);
        } else {
          alert('📭 No new responses found.\n\nAll pending emails still need follow-up.');
        }
      }
    } catch (error) {
      alert(`❌ Failed to check responses!\n\n${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const generateFollowups = async () => {
    if (!isGmailConnected) {
      alert('Please connect Gmail in Settings first!');
      return;
    }

    if (!apiKey) {
      alert('Please configure your AI API key in Settings!');
      return;
    }

    const confirmed = window.confirm(
      '📧 GENERATE FOLLOW-UPS\n\n' +
      'This will generate follow-up emails for all pending emails that:\n' +
      '• Were sent 3+ days ago\n' +
      '• Haven\'t received a response\n' +
      '• Need follow-up (max 2 per email)\n\n' +
      'All follow-ups will be saved as drafts.\n\n' +
      'Continue?'
    );

    if (!confirmed) return;

    setLoading(true);
    setLoadingText('📧 Generating follow-ups...');

    try {
      const response = await axios.post(`${API_URL}/followup/generate`, {
        sessionId: 'react-session',
        apiKey: apiKey
      });

      if (response.data.success) {
        loadFollowupStats();
        loadStats();
        
        alert(`✅ Follow-ups Generated!\n\n` +
              `Successfully created: ${response.data.generated} follow-ups\n` +
              `Total checked: ${response.data.total} emails\n\n` +
              `All saved to Gmail Drafts!`);
      }
    } catch (error) {
      alert(`❌ Failed to generate follow-ups!\n\n${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      {loading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm">
          <Loader2 className="h-12 w-12 animate-spin text-white" />
          <p className="mt-4 text-xl font-semibold text-white">{loadingText}</p>
        </div>
      )}

      <Header 
        title="Follow-up System" 
        subtitle="Automatic follow-ups for pending emails"
        stats={stats}
      />

      <div className="p-8 max-w-6xl mx-auto">
        <div className="space-y-6">
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card className="border-blue-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-600 mb-2">{followupStats.total_sent}</div>
                  <div className="text-sm text-gray-600">Total Sent</div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-green-600 mb-2">{followupStats.responses}</div>
                  <div className="text-sm text-gray-600">Responses</div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-purple-600 mb-2">{followupStats.followups_sent}</div>
                  <div className="text-sm text-gray-600">Follow-ups</div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-600 mb-2">{followupStats.pending_followups}</div>
                  <div className="text-sm text-gray-600">Pending</div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-emerald-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <div className="text-4xl font-bold text-emerald-600">{followupStats.response_rate}%</div>
                    <TrendingUp className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div className="text-sm text-gray-600">Response Rate</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Actions Card */}
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <RefreshCw className="h-6 w-6 text-indigo-600" />
                Follow-up Management
              </CardTitle>
              <CardDescription>
                Automatic follow-ups for emails that need attention (3-day intervals, max 2 follow-ups)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Action Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button
                  onClick={checkForResponses}
                  className="h-16 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-lg"
                  disabled={!isGmailConnected}
                >
                  <RefreshCw className="mr-2 h-6 w-6" />
                  Check for Responses
                </Button>
                
                <Button
                  onClick={generateFollowups}
                  className="h-16 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-lg"
                  disabled={!isGmailConnected || !apiKey}
                >
                  <Send className="mr-2 h-6 w-6" />
                  Generate Follow-ups
                </Button>
              </div>

              {(!apiKey || !isGmailConnected) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                  <p className="font-semibold mb-1">⚠️ Setup Required</p>
                  <p className="text-xs">
                    {!isGmailConnected && '• Connect Gmail in Settings\n'}
                    {!apiKey && '• Configure API key in Settings'}
                  </p>
                </div>
              )}

              {/* How it Works */}
              <div className="bg-indigo-50 rounded-lg p-6">
                <div className="font-semibold text-lg mb-4 flex items-center gap-2 text-indigo-900">
                  <Info className="h-5 w-5 text-indigo-600" />
                  How Follow-ups Work:
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</div>
                      <div>
                        <div className="font-semibold text-indigo-900">Check Responses</div>
                        <div className="text-xs text-gray-600">Scan Gmail inbox for replies to your sent emails</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</div>
                      <div>
                        <div className="font-semibold text-indigo-900">First Follow-up</div>
                        <div className="text-xs text-gray-600">Sent after 3 days if no response received</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</div>
                      <div>
                        <div className="font-semibold text-indigo-900">Second Follow-up</div>
                        <div className="text-xs text-gray-600">Final attempt after 6 days total (3 days after first)</div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">4</div>
                      <div>
                        <div className="font-semibold text-indigo-900">Smart AI Generation</div>
                        <div className="text-xs text-gray-600">References previous conversation naturally</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">5</div>
                      <div>
                        <div className="font-semibold text-indigo-900">Gentle & Professional</div>
                        <div className="text-xs text-gray-600">Maintains friendly tone throughout</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">6</div>
                      <div>
                        <div className="font-semibold text-indigo-900">Auto Draft Creation</div>
                        <div className="text-xs text-gray-600">All follow-ups saved to Gmail Drafts</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Summary */}
              <div className="bg-white border border-indigo-200 rounded-lg p-6">
                <h3 className="font-semibold text-lg text-gray-900 mb-4">Current Status:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-700">Total emails sent:</span>
                    <span className="font-bold text-blue-600">{followupStats.total_sent}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-700">Received responses:</span>
                    <span className="font-bold text-green-600">{followupStats.responses}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-700">Follow-ups sent:</span>
                    <span className="font-bold text-purple-600">{followupStats.followups_sent}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-700">Pending follow-ups:</span>
                    <span className="font-bold text-orange-600">{followupStats.pending_followups}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tips Card */}
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-3">💡 Pro Tips:</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Check for responses regularly to keep your tracking accurate</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Generate follow-ups once or twice a day for best results</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Review generated drafts before sending</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Maximum 2 follow-ups per email to avoid being pushy</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FollowUpSystem;

